import { useEffect, useState } from "react";
import { useStore } from "../lib/store";
import { api } from "../lib/api";
import { isTauriSync } from "../lib/tauri";
import { isCapacitor } from "../lib/capacitor";
import { getFcmPermissionStatus } from "../lib/fcm";
import { enablePushNotifications } from "../lib/pwa";
import type { PresenceKind } from "@swift/shared";

const KINDS: Array<{ value: PresenceKind; label: string; dot: string }> = [
  { value: "online", label: "Online", dot: "bg-brand-green" },
  { value: "away", label: "Afwezig", dot: "bg-yellow-500" },
  { value: "busy", label: "Bezet", dot: "bg-red-500" },
];

// Snelle status-presets: één klik zet zowel een passend status-bericht
// als de bijbehorende presence-stip. Houdt de balk zakelijk en snel.
const PRESETS: Array<{ label: string; text: string; kind: PresenceKind }> = [
  { label: "In vergadering", text: "In vergadering", kind: "busy" },
  { label: "Lunch", text: "Lunch", kind: "away" },
  { label: "Verlof", text: "Met verlof", kind: "away" },
];

/** Vertaalt de fout-reden uit enablePushNotifications naar een bruikbare
 *  Nederlandse uitleg. De reasons komen uit lib/pwa.ts (Web Push) en
 *  lib/fcm.ts (Android/FCM). */
function explainPushFailure(reason: string): string {
  switch (reason) {
    case "denied":
      return "Meldingen zijn geweigerd. Zet ze aan via Android-instellingen → Apps → Swift → Meldingen (of de site-instellingen van je browser) en probeer opnieuw.";
    case "server_failed":
      return "De server accepteerde de meldings-registratie niet. Waarschijnlijk ontbreekt FCM_SERVICE_ACCOUNT_JSON (Android) of de VAPID-configuratie (browser) op de server — waarschuw je beheerder.";
    case "registration_error":
      return "Registratie bij Firebase mislukte. Is deze APK gebouwd mét google-services.json? Zonder dat bestand kunnen er geen meldingen binnenkomen.";
    case "timeout":
      return "Geen reactie van de meldingsdienst (timeout). Controleer je internetverbinding en probeer het opnieuw.";
    case "no_vapid":
      return "De server heeft geen Web-Push-configuratie (VAPID-keys). Waarschuw je beheerder.";
    default:
      return `Meldingen aanzetten is mislukt (${reason}). Probeer het opnieuw of waarschuw je beheerder.`;
  }
}

export default function StatusBar({ onLogout }: { onLogout(): void }) {
  const me = useStore((s) => s.me);
  const connection = useStore((s) => s.connection);
  const setMe = useStore((s) => s.setMe);
  const [editingText, setEditingText] = useState(false);
  const [statusText, setStatusText] = useState(me?.status_text ?? "");

  // Push-status. In PWA-browser-mode lezen we Notification.permission;
  // in Capacitor-Android-mode lezen we de FCM-permissie via een aparte
  // check (Notification-API werkt daar wel maar reflecteert FCM-status
  // niet één-op-één). "default"/"prompt" = nog niet gevraagd → knop tonen.
  type PushState = NotificationPermission | "unsupported" | "prompt";
  const [pushPerm, setPushPerm] = useState<PushState>(
    isCapacitor()
      ? "prompt"
      : typeof window !== "undefined" && "Notification" in window
      ? Notification.permission
      : "unsupported",
  );
  const [pushBusy, setPushBusy] = useState(false);

  useEffect(() => {
    if (isTauriSync()) return;
    if (isCapacitor()) {
      void getFcmPermissionStatus().then((s) => setPushPerm(s));
      return;
    }
    if (typeof window === "undefined" || !("Notification" in window)) return;
    // Permissie kan veranderen via browser-instellingen; korte
    // re-check bij focus.
    const onVis = () => setPushPerm(Notification.permission);
    window.addEventListener("focus", onVis);
    return () => window.removeEventListener("focus", onVis);
  }, []);

  const showPushButton =
    !isTauriSync() && (pushPerm === "default" || pushPerm === "prompt");

  async function tryEnablePush() {
    setPushBusy(true);
    try {
      const r = await enablePushNotifications();
      if (isCapacitor()) {
        // Alleen bij een échte permissie-weigering verdwijnt de knop;
        // bij een server/registratie-fout blijft 'ie staan zodat een
        // nieuwe poging mogelijk is nadat het probleem is opgelost.
        setPushPerm(r.ok ? "granted" : r.reason === "denied" ? "denied" : "prompt");
      } else if (typeof Notification !== "undefined") {
        setPushPerm(Notification.permission);
      }
      // Falen is stil geweest tot nu toe — daardoor leek "aanzetten" te
      // lukken terwijl het token nooit bij de server aankwam en er dus
      // nooit een melding zou volgen. Vertel de gebruiker wat er mis is
      // (en wat eraan te doen is) i.p.v. de knop stil te laten verdwijnen.
      if (!r.ok && r.reason !== "tauri") {
        alert(explainPushFailure(r.reason));
      }
    } finally {
      setPushBusy(false);
    }
  }

  if (!me) return null;

  async function setKind(kind: PresenceKind) {
    const { user } = await api.updateMe({ status_kind: kind });
    setMe(user);
  }
  async function setText() {
    const { user } = await api.updateMe({ status_text: statusText.trim() || null });
    setMe(user);
    setEditingText(false);
  }
  async function applyPreset(p: { text: string; kind: PresenceKind }) {
    const { user } = await api.updateMe({ status_text: p.text, status_kind: p.kind });
    setMe(user);
  }

  return (
    <div className="border-t border-slate-700 bg-slate-800 p-3 text-sm">
      <div className="mb-2 flex items-center gap-2">
        <span
          className={`h-2 w-2 rounded-full ${
            KINDS.find((k) => k.value === me.status_kind)?.dot ?? "bg-slate-500"
          }`}
        />
        {/* Geen bewerkbaar veld meer: de naam komt uit de planning en verandert
            mee zodra hij daar verandert. */}
        <span className="flex min-w-0 flex-1 items-baseline gap-1 truncate">
          <span className="truncate font-medium text-slate-200">{me.display_name}</span>
          <span className="text-xs text-slate-500">@{me.username}</span>
        </span>
        <span className="ml-auto text-[10px] uppercase tracking-wider text-slate-500">
          {connection === "connected" ? "verbonden" : connection === "connecting" ? "verbinden…" : "offline"}
        </span>
      </div>
      <div className="mb-2 flex gap-1">
        {KINDS.map((k) => (
          <button
            key={k.value}
            onClick={() => setKind(k.value)}
            className={`flex-1 rounded px-2 py-1 text-xs ${
              me.status_kind === k.value
                ? "bg-slate-700 text-slate-100"
                : "text-slate-400 hover:bg-slate-700"
            }`}
          >
            {k.label}
          </button>
        ))}
      </div>
      <div className="mb-2 flex flex-wrap gap-1">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            onClick={() => void applyPreset(p)}
            className="rounded-full border border-slate-700 px-2 py-0.5 text-[11px] text-slate-400 hover:bg-slate-700 hover:text-slate-200"
            title={`Status: ${p.text}`}
          >
            {p.label}
          </button>
        ))}
      </div>
      {editingText ? (
        <div className="flex gap-1">
          <input
            className="flex-1 rounded border border-slate-700 bg-slate-900 px-2 py-1 text-xs text-slate-200 outline-none"
            value={statusText}
            onChange={(e) => setStatusText(e.target.value)}
            maxLength={140}
            placeholder="bv. lunch tot 13u"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void setText();
              if (e.key === "Escape") setEditingText(false);
            }}
          />
          <button
            className="shrink-0 rounded bg-accent px-2 py-1 text-xs font-medium text-white hover:bg-accent-hover"
            onClick={setText}
          >
            OK
          </button>
        </div>
      ) : (
        <button
          onClick={() => {
            setStatusText(me.status_text ?? "");
            setEditingText(true);
          }}
          className="block w-full truncate text-left text-xs text-slate-400 hover:text-slate-200"
          title="Klik om status-tekst aan te passen"
        >
          {me.status_text || "Stel een status-bericht in…"}
        </button>
      )}
      {showPushButton && (
        <button
          onClick={() => void tryEnablePush()}
          disabled={pushBusy}
          className="mb-2 flex w-full items-center justify-center gap-2 rounded border border-accent/40 bg-accent/10 px-2 py-1.5 text-xs font-medium text-slate-200 hover:bg-accent/20 disabled:opacity-50"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
            <path d="M13.73 21a2 2 0 0 1-3.46 0" />
          </svg>
          {pushBusy ? "Bezig…" : "Meldingen aanzetten"}
        </button>
      )}
      <div className="mt-3 flex gap-1">
        <button
          className="flex-1 rounded bg-slate-700 px-2 py-1 text-xs text-slate-300 hover:bg-slate-600"
          onClick={onLogout}
        >
          Uitloggen
        </button>
        <button
          className="flex h-7 w-8 items-center justify-center rounded bg-slate-700 text-slate-300 hover:bg-slate-600"
          onClick={() => useStore.getState().openMentions()}
          title="Mijn vermeldingen"
          aria-label="Mijn vermeldingen"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M16 8v5a3 3 0 0 0 6 0v-1a10 10 0 1 0-3.92 7.94" />
          </svg>
        </button>
        <button
          className="flex h-7 w-8 items-center justify-center rounded bg-slate-700 text-slate-300 hover:bg-slate-600"
          onClick={() => useStore.getState().openSettings()}
          title="Instellingen"
          aria-label="Instellingen"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
          </svg>
        </button>
      </div>
    </div>
  );
}
